import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import { Button, ButtonProps, Spinner } from "@heroui/react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { download } from "@/lib/fetch";

type DownloadButtonProps = {
  href: string;
  filename?: string;
  isIconOnly?: boolean;
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
  color?: ButtonProps["color"];
  textKey?: string;
  text?: string;
  className?: string;
};

const sizeMap: Record<string, string> = {
  sm: "size-4",
  md: "size-5",
  lg: "size-6",
};

export const DownloadButton = ({
  href,
  filename,
  isIconOnly = false,
  size = "sm",
  variant = "light",
  color = "primary",
  textKey = "actions.download",
  text,
  className,
}: DownloadButtonProps) => {
  const { t } = useTranslation();

  const downloadMutation = useMutation({
    mutationFn: async () => {
      await download(href, filename);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t("errors.unexpected"));
    },
  });

  const label = text ?? t(textKey);

  return (
    <Button
      isIconOnly={isIconOnly}
      size={size}
      variant={variant}
      color={color}
      onPress={() => downloadMutation.mutate()}
      isDisabled={downloadMutation.isPending}
      aria-label={label}
      className={className}
    >
      {downloadMutation.isPending ? (
        <Spinner color={color} size={size} />
      ) : isIconOnly ? (
        <ArrowDownTrayIcon
          className={`${sizeMap[size] || "size-4"} text-primary`}
        />
      ) : (
        <>
          <ArrowDownTrayIcon className={`${sizeMap[size] || "size-4"} mr-1`} />
          {label}
        </>
      )}
    </Button>
  );
};

export default DownloadButton;
